import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCrosshairs } from 'react-icons/fa';
import { playSoundEffect } from '../hooks/useSoundEffects';
import './DamageAnimation.css';

export function unitHasSniperAbility(unit) {
  if (!unit) return false;
  if (Array.isArray(unit.abilities) && unit.abilities.includes('Sniper')) {
    return true;
  }
  if (Array.isArray(unit.instanceAbilities) && unit.instanceAbilities.includes('Sniper')) {
    return true;
  }
  return false;
}

export function isSniperLethalKillOutcome(outcome, isGeneralAttack = false) {
  if (isGeneralAttack || !outcome) return false;
  return unitHasSniperAbility(outcome.attacker)
    && outcome.targetDestroyed === true
    && typeof outcome.damageToTarget === 'number'
    && outcome.damageToTarget > 0;
}

export function unitHasPenetrateAbility(unit) {
  if (!unit) return false;
  if (unit.confused === true || unit.isConfused === true) {
    return false;
  }
  if (Array.isArray(unit.abilities) && unit.abilities.includes('Penetrate')) {
    return true;
  }
  if (Array.isArray(unit.instanceAbilities) && unit.instanceAbilities.includes('Penetrate')) {
    return true;
  }
  return false;
}

/** Health actually removed from the unit (not raw attack rating when overkill is redirected). */
export function getActualUnitDamageFromOutcome(outcome) {
  if (!outcome) return 0;
  if (outcome.isGeneralAttack || outcome.targetZone === 'general') {
    return typeof outcome.damageToTarget === 'number' ? outcome.damageToTarget : 0;
  }
  if (
    typeof outcome.targetHealthBefore === 'number'
    && typeof outcome.targetHealthAfter === 'number'
  ) {
    return Math.max(0, outcome.targetHealthBefore - outcome.targetHealthAfter);
  }
  return typeof outcome.damageToTarget === 'number' ? outcome.damageToTarget : 0;
}

/** Mirrors Penetrate.calculateOverflowDamage using attack-outcome snapshots. */
export function calculatePenetrateOverflowFromOutcome(outcome) {
  if (!outcome?.targetDestroyed || !unitHasPenetrateAbility(outcome.attacker)) {
    return 0;
  }
  const targetHealth = typeof outcome.targetCurrentHealth === 'number'
    ? outcome.targetCurrentHealth
    : outcome.targetHealthBefore;
  if (typeof targetHealth !== 'number') {
    return 0;
  }
  const attackRating = typeof outcome.attacker.attack_rating === 'number'
    ? Math.max(0, outcome.attacker.attack_rating)
    : 0;
  return Math.max(0, attackRating - targetHealth);
}

const EXPLOSION_IMAGE = '/animations/explosion.png';
const BASE_DURATION_MS = 900;

/** Percent offsets within the order card container — none at exact center (50, 50). */
export const DAMAGE_INDICATOR_POSITIONS = Object.freeze([
  { x: 22, y: 28 },
  { x: 78, y: 28 },
  { x: 28, y: 72 },
  { x: 72, y: 72 },
  { x: 62, y: 44 },
]);

export function pickRandomDamageIndicatorPosition() {
  const index = Math.floor(Math.random() * DAMAGE_INDICATOR_POSITIONS.length);
  return DAMAGE_INDICATOR_POSITIONS[index];
}

/** Prevent duplicate damage.mp3 when the same animationKey re-mounts or re-renders. */
const damageSoundPlayedKeys = new Set();

function playDamageAnimationSound(animationKey) {
  if (!animationKey || damageSoundPlayedKeys.has(animationKey)) {
    return;
  }
  damageSoundPlayedKeys.add(animationKey);
  try {
    playSoundEffect('/sounds/damage.mp3', 0.6);
  } catch (error) {
    console.error('DamageAnimation: error playing damage sound', error);
  }
  window.setTimeout(() => {
    damageSoundPlayedKeys.delete(animationKey);
  }, 5000);
}

/**
 * DamageAnimation — brief explosion.png pop with optional damage number (e.g. "-4").
 * Picks one of five non-center positions on the order card for each instance.
 * @param {Object} props
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {string|number} props.animationKey - Unique key for this animation instance
 * @param {number} [props.damageAmount] - Damage to display (shows "-N" when > 0)
 * @param {boolean} [props.shieldDeflected=false] - Shield blocked the hit (shows localized DEFLECT label)
 * @param {boolean} [props.sniperLethalKill=false] - Sniper lethal kill (shows red crosshair instead of damage)
 * @param {number} [props.animationSpeedMultiplier=1] - Speed multiplier (1 = normal)
 */
export const DamageAnimation = ({
  onComplete,
  animationKey,
  damageAmount = null,
  shieldDeflected = false,
  sniperLethalKill = false,
  animationSpeedMultiplier = 1,
}) => {
  const { t } = useTranslation();
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const { x, y } = useMemo(
    () => pickRandomDamageIndicatorPosition(),
    [animationKey]
  );

  const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
  const durationMs = Math.round(BASE_DURATION_MS / speedFactor);

  useEffect(() => {
    if (!animationKey) return undefined;

    completedRef.current = false;
    playDamageAnimationSound(animationKey);

    const timer = window.setTimeout(() => {
      if (completedRef.current) return;
      completedRef.current = true;
      onCompleteRef.current?.();
    }, durationMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [animationKey, durationMs]);

  const left = `${x}%`;
  const top = `${y}%`;
  const showDeflect = shieldDeflected === true;
  const showSniperTarget = sniperLethalKill === true && !showDeflect;
  const showNumber = typeof damageAmount === 'number' && damageAmount > 0 && !showDeflect && !showSniperTarget;

  return (
    <div
      className="damage-animation-root"
      style={{
        position: 'absolute',
        left,
        top,
        transform: 'translate(-50%, -50%)',
        zIndex: 10000,
        pointerEvents: 'none',
        width: '100%',
        height: '100%',
      }}
      data-animation-key={animationKey}
      data-debug-position={`percent:${x},${y}`}
    >
      <div
        className="damage-animation-burst"
        style={{ '--damage-anim-duration': `${durationMs}ms` }}
      >
        <img
          src={EXPLOSION_IMAGE}
          alt=""
          className="damage-animation-image"
          draggable={false}
        />
        {showNumber && (
          <span className="damage-animation-number">-{damageAmount}</span>
        )}
      </div>
      {showDeflect && (
        <span className="damage-animation-deflect">
          {t('battleUI.damageAnimation.deflect')}
        </span>
      )}
      {showSniperTarget && (
        <span className="damage-animation-sniper-target" aria-hidden>
          <FaCrosshairs />
        </span>
      )}
    </div>
  );
};

export default DamageAnimation;
